import type { SandboxFileInput, SandboxFolderInput, SandboxSystemsInput } from '../transpiler';
import { InvalidProjectPathError, normalizeProjectPath } from './paths';

/** The three folders a v1 sandbox project has. */
export type V1Folder = 'client' | 'server' | 'shared';

/** v1 load order: what `buildVirtualFiles` loads first — and so the order systems register in. */
const LOAD_ORDER: V1Folder[] = ['shared', 'client', 'server'];

const emptyFolder = (): SandboxFolderInput => ({ rootFiles: [], subfolders: {} });

/**
 * The file list of `subfolder`, created as the folder's OWN entry — a folder may be named `constructor`, `toString` or
 * `__proto__`, which a plain object already answers to (and `__proto__` would not even assign).
 */
function subfolderFiles(folder: SandboxFolderInput, subfolder: string): SandboxFileInput[] {
  if (!Object.hasOwn(folder.subfolders, subfolder)) {
    Object.defineProperty(folder.subfolders, subfolder, { value: [], enumerable: true, writable: true, configurable: true });
  }
  return folder.subfolders[subfolder];
}

/** Where a v1 file lives in the project tree: `src/<folder>[/<subfolder>]/<name>.ts`. */
export function treePathOf(folder: V1Folder, subfolder: string | null, name: string): string {
  return normalizeProjectPath(['src', folder, ...(subfolder ? [subfolder] : []), `${name}.ts`].join('/'));
}

/**
 * A tree path's place in the v1 model, or `null` when v1 cannot hold it: v1 holds `.ts` files under
 * `src/client`, `src/server` and `src/shared`, at any depth (a nested folder is a subfolder key like `"a/b"`).
 */
export function v1LocationOf(path: string): { folder: V1Folder; subfolder: string | null; name: string } | null {
  const match = /^src\/(client|server|shared)\/(.+)\.ts$/.exec(normalizeProjectPath(path));
  if (!match) return null;
  const segments = match[2].split('/');
  const name = segments.pop()!;
  return { folder: match[1] as V1Folder, subfolder: segments.length ? segments.join('/') : null, name };
}

/**
 * The v1 view of a project tree: every file v1 can hold, in tree order, except the `exclude`d paths (compared in their
 * one spelling, so `./src/a.ts` excludes `src/a.ts`), and an empty subfolder for each of `folders` under
 * `src/{client,server,shared}/` that holds no file of the view.
 */
export function toV1View(
  files: Record<string, string>,
  exclude: ReadonlySet<string> = new Set(),
  folders: ReadonlyArray<string> = [],
): SandboxSystemsInput {
  const excluded = new Set([...exclude].map(normalizeProjectPath));
  const view: SandboxSystemsInput = { client: emptyFolder(), server: emptyFolder(), shared: emptyFolder() };
  for (const [path, code] of Object.entries(files)) {
    if (excluded.has(normalizeProjectPath(path))) continue;
    const location = v1LocationOf(path);
    if (!location) continue;
    const folder = view[location.folder];
    if (location.subfolder === null) folder.rootFiles.push({ name: location.name, code });
    else subfolderFiles(folder, location.subfolder).push({ name: location.name, code });
  }
  for (const path of folders) {
    const match = /^src\/(client|server|shared)\/(.+)$/.exec(normalizeProjectPath(path));
    if (match) subfolderFiles(view[match[1] as V1Folder], match[2]);
  }
  return view;
}

/**
 * A v1 project's files as tree entries, inserted in v1 load order (shared, client, server; root files, then subfolders
 * in their key order), so the tree's order is the order the runtime loads — and registers systems — in. A v1 file's
 * `errors` (editor state) and an empty subfolder (a tree has no empty folders) do not carry over. Two v1 files that
 * land on one tree path (a root file named `systems/Move` and `Move` in `systems`) are refused, never merged by
 * keeping the last.
 */
export function fromV1View(systems: SandboxSystemsInput): Record<string, string> {
  const files: Record<string, string> = {};
  const place = (path: string, code: string) => {
    if (Object.hasOwn(files, path)) throw new InvalidProjectPathError(path, 'two files of the project land on it');
    files[path] = code;
  };
  for (const folder of LOAD_ORDER) {
    const source = systems[folder];
    for (const file of source.rootFiles) place(treePathOf(folder, null, file.name), file.code);
    for (const [subfolder, list] of Object.entries(source.subfolders)) {
      for (const file of list) place(treePathOf(folder, subfolder, file.name), file.code);
    }
  }
  return files;
}

/**
 * The v1 subfolders that hold no file, as tree folder paths (`src/client/components`), in v1 load order. A tree of
 * files cannot hold an empty folder, so a project keeps these beside its files (`MigratedProject.folders`).
 */
export function emptyFoldersOf(systems: SandboxSystemsInput): string[] {
  const folders: string[] = [];
  for (const folder of LOAD_ORDER) {
    for (const [subfolder, list] of Object.entries(systems[folder].subfolders)) {
      if (list.length === 0) folders.push(normalizeProjectPath(['src', folder, subfolder].join('/')));
    }
  }
  return folders;
}
